import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Form, Overlay, Popover } from 'react-bootstrap';
import { useRepeatingInvoice } from '../contexts/RepeatingInvoiceContext';
import { useAppData } from '../../../providers/AppDataProvider';
import { RepeatingInvoice, LineAmountType } from '../../../types/accounting';

interface LineRow {
  id: string;
  description: string;
  quantity: string;
  unitAmount: string;
}

interface CreateRepeatingInvoiceModalProps {
  show: boolean;
  onHide: () => void;
  editRepeating?: RepeatingInvoice | null;
}

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

export function CreateRepeatingInvoiceModal({ show, onHide, editRepeating }: CreateRepeatingInvoiceModalProps) {
  const { createRepeatingInvoice, updateRepeatingInvoice, actionLoading } = useRepeatingInvoice();
  const { customers } = useAppData();

  const isEdit = !!editRepeating;

  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [templateType, setTemplateType] = useState<'invoice' | 'bill'>('invoice');
  const [lineAmountTypes, setLineAmountTypes] = useState<LineAmountType>('Exclusive');
  const [frequency, setFrequency] = useState<'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY'>('MONTHLY');
  const [scheduleDate, setScheduleDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reference, setReference] = useState('');
  const [showDatePopover, setShowDatePopover] = useState(false);
  const dateInfoRef = useRef<HTMLButtonElement>(null);
  const [status, setStatus] = useState<'ACTIVE' | 'PAUSED'>('PAUSED');
  const [lineItems, setLineItems] = useState<LineRow[]>([
    { id: generateId(), description: '', quantity: '1', unitAmount: '' },
  ]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      if (editRepeating) {
        setSelectedCustomerId(editRepeating.contactId || '');
        setTemplateType(editRepeating.templateType || 'invoice');
        setLineAmountTypes(editRepeating.lineAmountTypes || 'Exclusive');
        setFrequency((editRepeating.frequency as any) || 'MONTHLY');
        setScheduleDate(editRepeating.nextDate || editRepeating.startDate || '');
        setEndDate(editRepeating.endDate || '');
        setReference(editRepeating.reference || '');
        setStatus(editRepeating.status === 'ACTIVE' ? 'ACTIVE' : 'PAUSED');
        if (editRepeating.lineItems && editRepeating.lineItems.length > 0) {
          setLineItems(
            editRepeating.lineItems.map((li) => ({
              id: generateId(),
              description: li.description || '',
              quantity: String(li.quantity ?? 1),
              unitAmount: String(li.unitAmount ?? 0),
            }))
          );
        }
      } else {
        setSelectedCustomerId('');
        setTemplateType('invoice');
        setLineAmountTypes('Exclusive');
        setFrequency('MONTHLY');
        setScheduleDate('');
        setEndDate('');
        setReference('');
        setStatus('PAUSED');
        setLineItems([{ id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
      }
      setError(null);
    }
  }, [show, editRepeating]);

  const addLine = () => {
    setLineItems((prev) => [...prev, { id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
  };

  const removeLine = (id: string) => {
    if (lineItems.length <= 1) return;
    setLineItems((prev) => prev.filter((r) => r.id !== id));
  };

  const updateLine = (id: string, field: keyof LineRow, value: string) => {
    setLineItems((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const customer = customers.find(c => c.id === selectedCustomerId);
    if (!isEdit && !customer) {
      setError('Please select a customer.');
      return;
    }

    const items = lineItems
      .map((r) => ({
        description: r.description.trim(),
        quantity: parseFloat(r.quantity) || 1,
        unitAmount: parseFloat(r.unitAmount) || 0,
      }))
      .filter((r) => r.description || r.unitAmount > 0);
    if (items.length === 0) {
      setError('Add at least one line item');
      return;
    }

    try {
      if (isEdit && editRepeating) {
        await updateRepeatingInvoice(editRepeating.id, {
          templateType,
          lineAmountTypes,
          frequency,
          startDate: scheduleDate || undefined,
          endDate: endDate || undefined,
          nextDate: scheduleDate || undefined,
          reference: reference.trim() || undefined,
          status,
          lineItems: items,
        });
      } else {
        await createRepeatingInvoice({
          contactId: selectedCustomerId,
          contactName: customer?.name || customer?.company || '',
          templateType,
          lineAmountTypes,
          frequency,
          startDate: scheduleDate || undefined,
          endDate: endDate || undefined,
          reference: reference.trim() || undefined,
          status,
          lineItems: items,
        });
      }
      onHide();
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    }
  };

  const totals = useMemo(() => {
    const subtotal = lineItems.reduce((sum, row) => {
      const qty = parseFloat(row.quantity) || 0;
      const unit = parseFloat(row.unitAmount) || 0;
      return sum + qty * unit;
    }, 0);
    if (lineAmountTypes === 'NoTax') return { subtotal, gst: 0, total: subtotal };
    if (lineAmountTypes === 'Inclusive') {
      const gst = Math.round((subtotal / 11) * 100) / 100;
      return { subtotal, gst, total: subtotal };
    }
    // Exclusive
    const gst = Math.round(subtotal * 0.1 * 100) / 100;
    return { subtotal, gst, total: subtotal + gst };
  }, [lineItems, lineAmountTypes]);

  const fmt = (n: number) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

  const customerOptions = customers
    .filter(c => c.name || c.company)
    .sort((a, b) => (a.name || a.company || '').localeCompare(b.name || b.company || ''));

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? `Edit recurring ${templateType}` : `New recurring ${templateType}`}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger py-2 mb-3">
              <i className="bi bi-exclamation-triangle me-2" />
              {error}
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Template Type</Form.Label>
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`btn btn-sm flex-grow-1 ${templateType === 'invoice' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setTemplateType('invoice')}
              >
                <i className="bi bi-receipt me-2" />Invoice (Money In)
              </button>
              <button
                type="button"
                className={`btn btn-sm flex-grow-1 ${templateType === 'bill' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setTemplateType('bill')}
              >
                <i className="bi bi-cash-stack me-2" />Bill (Expense)
              </button>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{templateType === 'bill' ? 'Supplier' : 'Customer'}</Form.Label>
            {isEdit ? (
              <Form.Control
                type="text"
                value={editRepeating?.contactName ?? 'Unknown'}
                disabled
                className="bg-secondary text-muted"
              />
            ) : (
              <Form.Select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                aria-label="Select customer"
              >
                <option value="">Select a customer...</option>
                {customerOptions.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || c.company}</option>
                ))}
              </Form.Select>
            )}
            {isEdit && (
              <Form.Text className="text-muted">
                Customer cannot be changed on an existing recurring invoice.
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Frequency</Form.Label>
            <Form.Select value={frequency} onChange={(e) => setFrequency(e.target.value as any)}>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
              <option value="QUARTERLY">Quarterly</option>
              <option value="YEARLY">Yearly</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-1">
              <Form.Label className="mb-0">
                {isEdit ? 'Next generation date' : 'Start date'}
              </Form.Label>
              <button
                ref={dateInfoRef}
                type="button"
                className="btn btn-icon btn-sm btn-light-primary"
                style={{ width: 22, height: 22, padding: 0, borderRadius: '50%', lineHeight: 1 }}
                onClick={() => setShowDatePopover((v) => !v)}
                aria-label="Schedule date info"
              >
                <i className="bi bi-info" style={{ fontSize: '0.7rem' }} />
              </button>
              <Overlay
                target={dateInfoRef.current}
                show={showDatePopover}
                placement="right"
                rootClose
                onHide={() => setShowDatePopover(false)}
              >
                <Popover id="schedule-date-popover" style={{ maxWidth: 280 }}>
                  <Popover.Header as="h6" className="py-2">How the schedule date works</Popover.Header>
                  <Popover.Body className="fs-7 py-2">
                    <p className="mb-2">
                      This date is the <strong>recurring anchor</strong> — the day-of-month (or day-of-week/year) the schedule is pinned to.
                    </p>
                    <p className="mb-2">
                      Each time an invoice or bill is generated, the next run date automatically advances by one {frequency.charAt(0) + frequency.slice(1).toLowerCase()} period from this date.
                    </p>
                    <p className="mb-0">
                      To reschedule, simply set this to the date you want the <em>next</em> generation to occur. Future runs will then repeat from that new anchor.
                    </p>
                  </Popover.Body>
                </Popover>
              </Overlay>
            </div>
            <div className="row g-2">
              <div className="col-md-6">
                <Form.Control
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                />
                <Form.Text className="text-muted">
                  {isEdit ? 'When the next invoice/bill will be generated' : 'Date of the first invoice/bill'}
                </Form.Text>
              </div>
              <div className="col-md-6">
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} placeholder="End date (optional)" />
                <Form.Text className="text-muted">End date (optional)</Form.Text>
              </div>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Reference (optional)</Form.Label>
            <Form.Control value={reference} onChange={(e) => setReference(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value as any)}>
              <option value="PAUSED">Paused (draft)</option>
              <option value="ACTIVE">Active</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>GST Treatment</Form.Label>
            <Form.Select value={lineAmountTypes} onChange={(e) => setLineAmountTypes(e.target.value as LineAmountType)}>
              <option value="Exclusive">Tax Exclusive (GST added on top)</option>
              <option value="Inclusive">Tax Inclusive (GST included in price)</option>
              <option value="NoTax">No Tax</option>
            </Form.Select>
          </Form.Group>
          <Form.Label>Line items</Form.Label>
          {lineItems.map((row) => (
            <div key={row.id} className="d-flex gap-2 mb-2 align-items-start">
              <Form.Control placeholder="Description" value={row.description} onChange={(e) => updateLine(row.id, 'description', e.target.value)} className="flex-grow-1" />
              <Form.Control type="number" min="0" step="0.01" placeholder="Qty" style={{ width: '80px' }} value={row.quantity} onChange={(e) => updateLine(row.id, 'quantity', e.target.value)} />
              <Form.Control type="number" min="0" step="0.01" placeholder="Unit $" style={{ width: '100px' }} value={row.unitAmount} onChange={(e) => updateLine(row.id, 'unitAmount', e.target.value)} />
              <button type="button" className="btn btn-sm btn-icon btn-light-danger" onClick={() => removeLine(row.id)} disabled={lineItems.length <= 1}>
                <i className="bi bi-trash" />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-light-primary" onClick={addLine}>
            <i className="bi bi-plus me-1" /> Add line
          </button>

          <div className="mt-4 pt-3 border-top d-flex flex-column align-items-end gap-1">
            {lineAmountTypes !== 'NoTax' && (
              <div className="d-flex justify-content-between w-100" style={{ maxWidth: 260 }}>
                <span className="text-muted fs-7">
                  {lineAmountTypes === 'Inclusive' ? 'Subtotal (inc. GST)' : 'Subtotal (excl. GST)'}
                </span>
                <span className="fs-7">{fmt(totals.subtotal)}</span>
              </div>
            )}
            {lineAmountTypes !== 'NoTax' && (
              <div className="d-flex justify-content-between w-100" style={{ maxWidth: 260 }}>
                <span className="text-muted fs-7">
                  GST {lineAmountTypes === 'Inclusive' ? '(included)' : '(10%)'}
                </span>
                <span className="fs-7">{fmt(totals.gst)}</span>
              </div>
            )}
            <div className="d-flex justify-content-between w-100 fw-bold" style={{ maxWidth: 260 }}>
              <span>Total</span>
              <span>{fmt(totals.total)}</span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={onHide}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={!!actionLoading}>
            {actionLoading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
            {isEdit ? 'Update' : 'Create'}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default CreateRepeatingInvoiceModal;
